// apps/patient/src/components/appointments/JoinWaitlistPanel.tsx
// Patient-facing waitlist sign-up reached from the reschedule picker's
// "no open times in the next two weeks" notice (reminder link and portal).
// Only the patient's preferences are collected here; the server builds the
// waitlist row from the appointment (provider, type, case, contact info,
// priority high). Comments save to the staff-side Notes field.

import { useState } from 'react';
import { AlertCircle, Check, Phone } from 'lucide-react';
import TimePreferenceFields from '@prism-ehr/shared/components/scheduling/TimePreferenceFields';
import { serializeTimePreferences } from '@prism-ehr/shared/utils/availabilityEngine';
import { ManageAppointmentInfo, WaitlistJoinBody, readErrorMessage } from './manageTypes';

interface JoinWaitlistPanelProps {
  info: ManageAppointmentInfo;
  providerMode: 'current' | 'any';
  /** Shown to the patient: "Dr. Smith" or "Any of: A, B". */
  providerLabel: string;
  locationId: string | null;
  locationName: string | null;
  onSubmit: (body: WaitlistJoinBody) => Promise<any>;
  onBack: () => void;
}

interface TimeWindow {
  start: number;
  end: number | null;
  _invalidEnd?: boolean;
}

export function JoinWaitlistPanel({ info, providerMode, providerLabel, locationId, locationName, onSubmit, onBack }: JoinWaitlistPanelProps) {
  const [windows, setWindows] = useState<TimeWindow[]>([]);
  const [excludedDays, setExcludedDays] = useState<number[]>([]);
  const [earliestDate, setEarliestDate] = useState('');
  const [latestDate, setLatestDate] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        provider_mode: providerMode,
        location_id: locationId,
        time_preferences: serializeTimePreferences({
          windows: windows.filter(w => !w._invalidEnd).map(w => ({ start: w.start, end: w.end ?? null })),
          excludedDays
        }),
        earliest_date: earliestDate || null,
        latest_date: latestDate || null,
        comments: comments.trim() || null
      });
      setDone(true);
    } catch (err) {
      setError(await readErrorMessage(err, 'We could not add you to the waitlist. Please try again or call the office.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-green-900 text-center mb-2">You're on the waitlist</h3>
        <p className="text-green-800 text-center">
          The office will contact you when a time opens. Your current appointment is unchanged.
        </p>
        <div className="mt-4 text-sm text-green-800 text-center">
          <p className="font-medium">{info.organization_name}</p>
          {info.organization_phone && (
            <a href={`tel:${info.organization_phone}`} className="inline-flex items-center gap-1 underline">
              <Phone className="h-3.5 w-3.5" />
              {info.organization_phone}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={onBack}
          className="mt-6 w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          Back to appointment
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Join the waitlist</h3>
        <p className="text-sm text-blue-700">
          We will contact you when a time opens with {providerLabel}
          {locationName ? ` at ${locationName}` : ''}. Your current appointment stays as it is until then.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <TimePreferenceFields
          windows={windows}
          excludedDays={excludedDays}
          onChange={(next: { windows: TimeWindow[]; excludedDays: number[] }) => {
            setWindows(next.windows);
            setExcludedDays(next.excludedDays);
          }}
          label="Times that work for you"
          description="Pick the windows you are available, or a specific time. Leave empty if any time works."
          daysLabel="Days you cannot attend"
          daysDescription="Tap the days that do not work for you. Leave empty if any day works."
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Earliest date</label>
            <input
              type="date"
              value={earliestDate}
              onChange={(e) => setEarliestDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Latest date</label>
            <input
              type="date"
              value={latestDate}
              min={earliestDate || undefined}
              onChange={(e) => setLatestDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={submitting}
            rows={3}
            placeholder="Anything the office should know"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Adding you to the waitlist...' : 'Add me to the waitlist'}
      </button>
      <button
        type="button"
        onClick={onBack}
        disabled={submitting}
        className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Back
      </button>
    </div>
  );
}
